import { useState, useCallback } from 'react';
import { editLandingCarouselTESTAPI } from '../../../../../api';
import { LandingCarousel } from '../../../../../store';
import { isDebugging } from '../../../../../utility';

interface UseEditLandingCarouselReturn {
  editCarousels: (carousels: LandingCarousel[]) => Promise<{ success: boolean; message?: string }>;
  loading: boolean;
  error: string | null;
}

/**
 * Custom React hook to edit landing carousel data directly via API
 * @returns Object containing editCarousels function, loading state, and error state
 */
export const useEditLandingCarousel = (): UseEditLandingCarouselReturn => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const editCarousels = useCallback(async (carousels: LandingCarousel[]): Promise<{ success: boolean; message?: string }> => {
    setLoading(true);
    setError(null);

    try {
      // Transform carousels to API format (same logic as the store action)
      const carouselsToSend = carousels.map((c) => {
        return {
          id: c.id,
          image: (c.image || '').replace("'", "''"),
          link: (c.link || '').replace("'", "''"),
          button_text: (c.buttonText || '').replace("'", "''"),
          headline: (c.headerText || '').replace("'", "''"),
          subhead: (c.subHeaderText || '').replace("'", "''"),
          hide: c.isHidden ? 1 : 0,
          orderNum: c.orderNum
        };
      });

      if (isDebugging) {
        console.log('useEditLandingCarousel - input carousels:', carousels);
        console.log('useEditLandingCarousel - carouselsToSend:', carouselsToSend);
      }

      const res = await editLandingCarouselTESTAPI(carouselsToSend);

      if (isDebugging) {
        console.log('useEditLandingCarousel - API response:', res);
      }

      const successMessage = 'Successfully updated carousels';
      
      return { 
        success: true, 
        message: successMessage 
      };

    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err?.message || 'Failed to edit landing carousel';
      setError(errorMessage);
      
      if (isDebugging) {
        console.log('useEditLandingCarousel error:', err);
        console.log('Error details:', {
          status: err?.response?.status,
          statusText: err?.response?.statusText,
          data: err?.response?.data,
          message: err?.message
        });
      }

      return { 
        success: false, 
        message: errorMessage 
      };
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    editCarousels,
    loading,
    error
  };
};

export default useEditLandingCarousel;
